import { Alert as MantineAlert, AlertProps, useMantineTheme, Button, Group } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconInfoCircle, IconX } from '@tabler/icons-react';
import { forwardRef, ReactNode, useMemo } from 'react';
import FadeIn from '../../animations/transitions/FadeIn';
import SlideIn from '../../animations/transitions/SlideIn';
import ScaleIn from '../../animations/transitions/ScaleIn';
import { getContrastingTextColor } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type AlertVariant = 'default' | 'filled' | 'light' | 'outline' | 'gradient';
export type AnimationType = 'fade' | 'slide' | 'scale';

export interface ThemedAlertProps extends Omit<AlertProps, 'variant'> {
  alertVariant?: AlertVariant;
  gradientVariant?: GradientVariant;
  type?: 'success' | 'error' | 'warning' | 'info';
  withAnimation?: boolean;
  animationType?: AnimationType;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const Alert = forwardRef<HTMLDivElement, ThemedAlertProps>(
  (
    {
      alertVariant = 'light',
      gradientVariant = 'primary',
      type,
      icon,
      children,
      style,
      withAnimation = false,
      animationType = 'fade',
      action,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const typeConfig = {
      success: { color: 'green', icon: <IconCheck size={20} /> },
      error: { color: 'red', icon: <IconX size={20} /> },
      warning: { color: 'yellow', icon: <IconAlertCircle size={20} /> },
      info: { color: 'blue', icon: <IconInfoCircle size={20} /> },
    };

    const config = type ? typeConfig[type] : undefined;

    const renderAlert = () => {
      const alertContent = (
        <>
          {children}
          {action && (
            <Group mt="sm">
              <Button size="xs" variant="light" color={config?.color} onClick={action.onClick}>
                {action.label}
              </Button>
            </Group>
          )}
        </>
      );

      if (alertVariant === 'gradient') {
        return (
          <MantineAlert
            ref={ref}
            icon={icon || config?.icon}
            variant="filled"
            style={{
              backgroundImage: gradientBg,
              color: autoTextColor,
              ...accessibilityStyles,
              ...style,
            }}
            {...rest}
          >
            {alertContent}
          </MantineAlert>
        );
      }

      return (
        <MantineAlert
          ref={ref}
          icon={icon || config?.icon}
          variant={alertVariant}
          color={config?.color}
          style={{ ...accessibilityStyles, ...style }}
          {...rest}
        >
          {alertContent}
        </MantineAlert>
      );
    };

    if (!withAnimation) {
      return renderAlert();
    }

    // Wrap with animation component
    const AnimationWrapper =
      animationType === 'fade' ? FadeIn : animationType === 'slide' ? SlideIn : ScaleIn;

    return (
      <AnimationWrapper visible={true} duration={300}>
        {renderAlert()}
      </AnimationWrapper>
    );
  }
);

Alert.displayName = 'Alert';

export default Alert;
